// Per-Block form-effect registry (P-037.24 / D-132; P-037.28 / D-136
// adds the second entry). Each entry maps a Block ID to an invisible
// side-effect component that `<Form>` renders after its field tree.
// The component receives the Form's `value` + `onChange` and uses
// Preact hooks to dispatch async side-effects (typically auto-
// populating a `'derived'`-branded field).
//
// **Why a registry rather than a `<Form>` prop.** Per-Block behavior
// lives next to per-Block schemas. A single dispatch site in `<Form>`
// (one map lookup per render) is simpler than threading effects from
// each parent screen. Form stays generic — the eager bundle pulls in
// the orchestrator components, but heavy SDK chunks (firebase) stay
// lazy via dynamic `await import(...)` inside each effect.
//
// **Empty map default.** When a Block ID has no registered effect,
// Form renders nothing extra. The registry is `Partial<Record<...>>`
// so unrelated Blocks pay no runtime cost.
//
// Entries:
// - `big3-signature-events` → Big3CompositeOrchestrator (D-132)
// - `big-3-hero-image` → BigHeroOrchestrator (D-136)

import type { ComponentChild } from 'preact'

import { Big3CompositeOrchestrator } from './effects/big3-composite-orchestrator'
import { BigHeroOrchestrator } from './effects/big-hero-orchestrator'

export interface FormEffectProps {
  readonly value: unknown
  readonly onChange: (next: unknown) => void
}

export type FormEffectComponent = (props: FormEffectProps) => ComponentChild

export const BLOCK_FORM_EFFECTS: Readonly<Record<string, FormEffectComponent>> = Object.freeze({
  'big3-signature-events': Big3CompositeOrchestrator,
  'big-3-hero-image': BigHeroOrchestrator,
})
